/*============================================================================
  Focus trap accessibility component. 
============================================================================*/
class FocusTrap {
  constructor() {
    this.handler = null;
    this.container = null;
  }

  activate(container, elementToFocus) {
    this.deactivate();

    const focusableElements = container.querySelectorAll(
      'summary, a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    elementToFocus?.focus();

    this.handler = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable.focus();
        }
      }
    };

    this.container = container;
    container.addEventListener('keydown', this.handler);
  }

  deactivate(elementToFocus) {
    if (this.handler && this.container) {
      this.container.removeEventListener('keydown', this.handler);
      this.handler = null;
      this.container = null;
    }
    elementToFocus?.focus();
  }
}

/*============================================================================
  StickyHeader.
    Calculates own header height and manages scroll behavior.
============================================================================*/
class StickyHeader extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = this.closest('.section-header');
    this.headerBounds = {};
    
    this.setHeaderHeight();
    window.matchMedia('(max-width: 990px)').addEventListener('change', this.setHeaderHeight.bind(this));
    this.currentScrollTop = 0;
    this.onScrollHandler = this.onScroll.bind(this);
    
    window.addEventListener('scroll', this.onScrollHandler, false);
    this.createObserver();
  }

  setHeaderHeight() {
    document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
    
    // Calculate total height of all header group sections
    const headerGroupSections = document.querySelectorAll('.shopify-section-group-header-group');
    let totalHeight = 0;
    headerGroupSections.forEach(section => {
      totalHeight += section.offsetHeight;
    });
    document.documentElement.style.setProperty('--header-group-height', `${totalHeight}px`);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  createObserver() {
    let observer = new IntersectionObserver((entries, observer) => {
      this.headerBounds = entries[0].intersectionRect;
      observer.disconnect();
    });
    observer.observe(this.header);
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    if (scrollTop > this.headerBounds.bottom) {
      this.header.classList.add('scrolled-past-header');
    } else if (scrollTop <= this.headerBounds.top) {
      this.header.classList.remove('scrolled-past-header');
    }
    this.currentScrollTop = scrollTop;
  }
}

customElements.define('sticky-header', StickyHeader);



/*============================================================================
  MenuDrawer
============================================================================*/
class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');
    this.focusTrap = new FocusTrap();
    this.setupEventListeners();
  }

  setupEventListeners() {
    // ESC to close
    this.addEventListener('keyup', (e) => {
      if (e.key === 'Escape') {
        const openDetails = e.target.closest('details[open]');
        if (!openDetails) return;
        
        if (openDetails === this.mainDetailsToggle) {
          this.closeMenu(e, this.mainDetailsToggle.querySelector('summary'));
        } else {
          this.closeSubmenu(openDetails);
        }
      }
    });

    // Toggle menu
    const summaries = this.querySelectorAll('summary');
    summaries.forEach(summary => {
      summary.addEventListener('click', (e) => this.handleSummaryClick(e));
    });

    // Close buttons
    const closeButtons = this.querySelectorAll('button');
    closeButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        const details = e.currentTarget.closest('details');
        this.closeSubmenu(details);
      });
    });

    // Accessibility: Close on focus out
    this.addEventListener('focusout', (e) => {
      setTimeout(() => {
        if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement)) {
          this.closeMenu(new Event('focusout'), this.mainDetailsToggle.querySelector('summary'));
        }
      });
    });
  }

  handleSummaryClick(event) {
    const summary = event.currentTarget;
    const details = summary.parentElement;
    const isOpen = details.hasAttribute('open');

    if (details === this.mainDetailsToggle) {
      if (isOpen) {
        event.preventDefault();
        this.closeMenu(event, summary);
      } else {
        this.openMenu(summary);
      }
    } else {
      setTimeout(() => {
        details.classList.add('menu-opening');
        summary.setAttribute('aria-expanded', 'true');
      }, 10);
    }
  }

  openMenu(summaryElement) {
    this.mainDetailsToggle.classList.add('menu-opening');
    document.body.classList.add('menu-open');

    summaryElement.setAttribute('aria-expanded', 'true');
    this.focusTrap.activate(this.mainDetailsToggle, summaryElement);
  }

  closeMenu(event, elementToFocus = false) {
    if (event === undefined) return;

    document.body.classList.remove('menu-open');

    this.querySelectorAll('details[open]').forEach(details => {
      if (details !== this.mainDetailsToggle) {
        details.removeAttribute('open');
        details.classList.remove('menu-opening');
        details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
      }
    });

    if (elementToFocus) {
      elementToFocus.setAttribute('aria-expanded', 'false');
      this.focusTrap.deactivate(elementToFocus);
    }

    this.closeAnimation(this.mainDetailsToggle);
  }

  closeSubmenu(detailsElement) {
    detailsElement.classList.remove('menu-opening');
    detailsElement.removeAttribute('open');

    const summary = detailsElement.querySelector('summary');
    summary?.setAttribute('aria-expanded', 'false');
    this.focusTrap.deactivate(summary);
  }

  closeAnimation(detailsElement) {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reducedMotion) {
      detailsElement.classList.remove('menu-opening');
      detailsElement.removeAttribute('open');
      return;
    }

    detailsElement.classList.remove('menu-opening');

    detailsElement.addEventListener('transitionend', () => {
      detailsElement.removeAttribute('open');
    }, { once: true });
  }
}

customElements.define('menu-drawer', MenuDrawer);


/*============================================================================
  HeaderDrawer
    Sets header bottom position and viewport height variables
============================================================================*/
class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenu(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');

    this.borderOffset = this.borderOffset || this.closest('.header-wrapper')?.classList.contains('header-wrapper--border-bottom') ? 1 : 0;

    document.documentElement.style.setProperty('--header-bottom-position', `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`);

    this.header.classList.add('menu-open');
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    document.body.style.overflow = 'hidden';

    summaryElement.setAttribute('aria-expanded', 'true');
    this.focusTrap.activate(this.mainDetailsToggle, summaryElement);

    window.addEventListener('resize', this.onResize);
  }

  closeMenu(event, elementToFocus) {
    if (!elementToFocus) return;

    super.closeMenu(event, elementToFocus);

    this.header.classList.remove('menu-open');
    document.body.style.overflow = '';

    window.removeEventListener('resize', this.onResize);
  }

  // Use this syntax to make it accesible with 'this'
  onResize = () => {
    this.header && document.documentElement.style.setProperty('--header-bottom-position', `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`);

    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  }
}

customElements.define('header-drawer', HeaderDrawer);



/*============================================================================
  SliderComponent
    Requires:
      1. .slider div with .slide divs
      2. prev and next buttons with data-dir="prev" or "next"
      3. .pagination div with .pagination-dot buttons with data-slide="1"
      4. Optional: data-direction="vertical" for vertical sliders (default is horizontal)
============================================================================*/
class SliderComponent extends HTMLElement {
  constructor() {
    super();

    this.slider = this.querySelector('.slider');
    this.slides = this.querySelectorAll('.slide');
    this.prevBtn = this.querySelector('[data-dir="prev"]');
    this.nextBtn = this.querySelector('[data-dir="next"]');

    if (!this.slider) return;

    this.currentIndex = 0;
    this.slideWidth = 0;

    this.enableLoop = false;

    // Detect direction (horizontal or vertical)
    // First check flex-direction, then fall back to data attribute
    const computedStyle = getComputedStyle(this.slider);
    const flexDirection = computedStyle.flexDirection;
    this.isVertical = flexDirection === 'column' || flexDirection === 'column-reverse' || this.dataset.direction === 'vertical';

    // Accessibility
    this.tabIndex = 0;

    this.setAttribute('role', 'region');
    this.setAttribute('aria-roledescription', 'carousel');
    this.setAttribute('aria-orientation', this.isVertical ? 'vertical' : 'horizontal');

    this.slides.forEach((slide, index) => {
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', `${index + 1} of ${this.slides.length}`);
    });

    this.addEventListener('keydown', (e) => {
      if (this.isVertical) {
        if (e.key === 'ArrowDown') {
          this.move(1);
        }
        if (e.key === 'ArrowUp') {
          this.move(-1);
        }
      } else {
        if (e.key === 'ArrowRight') {
          this.move(1);
        }
        if (e.key === 'ArrowLeft') {
          this.move(-1);
        }
      }
    });

    this.init();
  }

  init() {
    this.calculate();

    this.prevBtn?.addEventListener('click', () => this.move(-1));
    this.nextBtn?.addEventListener('click', () => this.move(1));

    this.setupPagination();

    window.addEventListener('resize', () => {
      this.calculate();
    });

    let scrollTimeout;

    this.slider.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        this.update();
      }, 50);
    });

    this.update();
  }

  setupPagination() {
    this.paginationLinks = this.querySelectorAll('[data-slide]');

    if (this.paginationLinks.length === 0) return;

    this.paginationLinks.forEach((link, index) => {
      link.setAttribute('role', 'button');
      link.setAttribute('aria-label', `Go to slide ${index + 1}`);

      link.addEventListener('click', (e) => {
        e.preventDefault();
        this.goTo(index);
      });
    });
  }

  calculate() {
    this.visibleSlides = Array.from(this.slides)
      .filter(slide => this.isVertical ? slide.clientHeight > 0 : slide.clientWidth > 0);

    if (this.visibleSlides.length < 2) return;

    if (this.isVertical) {
      this.slideOffset = this.visibleSlides[1].offsetTop - this.visibleSlides[0].offsetTop;
      this.slidesPerPage = Math.floor(this.slider.clientHeight / this.slideOffset);
    } else {
      this.slideOffset = this.visibleSlides[1].offsetLeft - this.visibleSlides[0].offsetLeft;
      this.slidesPerPage = Math.floor(this.slider.clientWidth / this.slideOffset);
    }
    
    this.totalPages = this.visibleSlides.length - this.slidesPerPage + 1;
  }

  update() {
    const previousPage = this.currentPage;
    const scrollPosition = this.isVertical ? this.slider.scrollTop : this.slider.scrollLeft;
    this.currentPage = Math.round(scrollPosition / this.slideOffset) + 1;

    if (previousPage !== this.currentPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.visibleSlides[this.currentPage - 1]
          }
        })
      );
    }

    if (!this.enableLoop) {
      if (this.currentPage <= 1) {
        this.prevBtn?.setAttribute('disabled', true);
      } else {
        this.prevBtn?.removeAttribute('disabled');
      }

      if (this.currentPage >= this.totalPages) {
        this.nextBtn?.setAttribute('disabled', true);
      } else {
        this.nextBtn?.removeAttribute('disabled');
      }
    } else {
      // Always enabled when looping
      this.prevBtn?.removeAttribute('disabled');
      this.nextBtn?.removeAttribute('disabled');
    }

    this.slides.forEach((slide, index) => {
      const isActive = index === this.currentPage - 1;
      
      slide.tabIndex = isActive ? 0 : -1;
    });

    // Update pagination dots
    if (this.paginationLinks && this.paginationLinks.length > 0) {
      this.paginationLinks.forEach((link, index) => {
        const isActive = index === this.currentPage - 1;
        link.classList.toggle('is-active', isActive);
        link.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }
  }

  move(direction) {
    if (this.stopAutoplay) {
      this.stopAutoplay();
      this.startAutoplay?.();
    }
    if (!this.slideOffset) return;

    const isFirstPage = this.currentPage === 1;
    const isLastPage = this.currentPage === this.totalPages;

    const currentScroll = this.isVertical ? this.slider.scrollTop : this.slider.scrollLeft;
    let newPosition;

    if (this.enableLoop) {
      if (isFirstPage && direction === -1) {
        // Jump to last
        newPosition = this.slideOffset * (this.totalPages - 1);
      } else if (isLastPage && direction === 1) {
        // Jump to first
        newPosition = 0;
      } else {
        newPosition = currentScroll + direction * this.slideOffset;
      }
    } else {
      newPosition = currentScroll + direction * this.slideOffset;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.isVertical) {
      this.slider.scrollTo({
        top: newPosition,
        behavior: prefersReducedMotion ? 'auto' : 'smooth'
      });
    } else {
      this.slider.scrollTo({
        left: newPosition,
        behavior: prefersReducedMotion ? 'auto' : 'smooth'
      });
    }
  }

  goTo(index) {
    if (!this.slideOffset) return;

    const position = this.slideOffset * index;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (this.isVertical) {
      this.slider.scrollTo({
        top: position,
        behavior: prefersReducedMotion ? 'auto' : 'smooth'
      });
    } else {
      this.slider.scrollTo({
        left: position,
        behavior: prefersReducedMotion ? 'auto' : 'smooth'
      });
    }
  }
}

customElements.define('slider-component', SliderComponent);



/*============================================================================
  SlideshowComponent

    Requires:
      1. .slider div with .slide divs
      2. prev and next buttons with data-dir="prev" or "next"
      3. .pagination div with buttons with data-slide="1"
      4. data-autoplay-interval in milliseconds
============================================================================*/
class SlideshowComponent extends SliderComponent {
  constructor() {
    super();

    this.enableLoop = true;
    
    const interval = Number(this.dataset.autoplayInterval);
    this.autoplaySpeed = interval || 3000;
    this.autoplay = !!interval; // only enable if attribute exists

    // Accessibility
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.autoplay = false;
    }

    this.setupAutoplayPaginationHandler();
    this.setupAutoplay();

    this.update();
  }

  setupAutoplayPaginationHandler() {
    // Add focus handler to pagination links to stop autoplay
    this.paginationLinks = this.querySelectorAll('[data-slide]');

    this.paginationLinks.forEach((link) => {
      link.addEventListener('focus', () => this.stopAutoplay());
    });
  }

  setupAutoplay() {
    if (!this.autoplay) return;

    this.startAutoplay();

    // Pause on hover
    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => this.startAutoplay());

    // Pause on focus (accessibility)
    this.addEventListener('focusin', () => this.stopAutoplay());
    this.addEventListener('focusout', () => this.startAutoplay());
  }

  startAutoplay() {
    if (this.autoplayTimer) return;

    this.autoplayTimer = setInterval(() => {
      this.move(1);
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;
  }
}

customElements.define('slideshow-component', SlideshowComponent);


/*============================================================================
  QuantityInput

    Requires:
      1. input element child with min, max, value, step, data-min='1'
      2. .quantity__button button child elements with name='minus' or 'plus'
============================================================================*/
class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  connectedCallback() {
    this.validateQtyRules();
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.currentTarget.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.currentTarget.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);


/*============================================================================
  ModalOpener

    Requires:
      1. data-modal='#PopupModal-id'
      2. Corresponding modal-dialog with same id as data-modal
============================================================================*/
class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);


/*============================================================================
  ModalDialog

    Requires:
      1. id='PopupModal-id'
      2. .close-modal-button button with ModalClose-id
============================================================================*/
class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);



/*============================================================================
  VariantSelects

    Requires:
      1. 
============================================================================*/
class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        }
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);


/*============================================================================
  Carousel - GSAP horizontalLoop wrapper

    Requires:
      1. .carousel container with .carousel-item elements as direct children
      3. Optional: prev and next buttons with data-dir='prev' or 'next'
      4. GSAP and horizontalLoop function must be loaded
============================================================================*/
class Carousel extends HTMLElement {
  constructor() {
    super();

    this.carousel = this.querySelector('.carousel');
    if (!this.carousel) return;

    this.itemSelector = '.carousel-item';
    this.items = gsap.utils.toArray(this.carousel.querySelectorAll(this.itemSelector));
    
    if (this.items.length === 0) return;

    this.prevBtn = this.querySelector('[data-dir="prev"]');
    this.nextBtn = this.querySelector('[data-dir="next"]');
    this.activeElement = null;

    this.setAttribute('role', 'region');
    this.setAttribute('aria-roledescription', 'carousel');

    this.init();
  }

  init() {
    this.loop = horizontalLoop(this.items, {
      paused: true,
      center: true,
      onChange: (element, index) => this.handleChange(element, index)
    });

    // Initialize to first item centered
    this.loop.toIndex(0, { duration: 0 });

    // Click on item to center it
    this.items.forEach((item, i) => {
      item.setAttribute('role', 'group');
      item.setAttribute('aria-roledescription', 'slide');
      item.setAttribute('aria-label', `Slide ${i + 1} of ${this.items.length}`);
      item.setAttribute('tabindex', i === 0 ? '0' : '-1');
      
      item.addEventListener('click', () => {
        this.loop.toIndex(i, { duration: 0.4, ease: 'power1.inOut' });
      });
    });

    // Button controls
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        this.loop.previous({ duration: 0.4, ease: 'power1.inOut' });
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        this.loop.next({ duration: 0.4, ease: 'power1.inOut' });
      });
    }

    this.addEventListener('keydown', (e) => {
      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          this.loop.next({ duration: 0.4, ease: 'power1.inOut' });
          break;

        case 'ArrowLeft':
          e.preventDefault();
          this.loop.previous({ duration: 0.4, ease: 'power1.inOut' });
          break;
      }
    });
  }

  handleChange(element, index) {
    this.items.forEach((item, i) => {
      item.classList.remove('active');
      item.setAttribute('aria-hidden', i === index ? 'false' : 'true');
      item.setAttribute('tabindex', i === index ? '0' : '-1');
    });

    element.classList.add('active');
    this.activeElement = element;

    // Dispatch custom event for external listeners
    this.dispatchEvent(
      new CustomEvent('carouselChange', {
        detail: {
          activeElement: element,
          activeIndex: index
        }
      })
    );
  }
}

customElements.define('carousel-component', Carousel);



class MarqueeComponent extends HTMLElement {
  constructor() {
    super();

    this.track = this.querySelector(".marquee__track");
    this.originalItems = Array.from(this.querySelectorAll(".marquee__item"));
    
    this.originalItems.forEach(item => item.classList.add('marquee__item--original'));
    
    this.lastWidth = this.offsetWidth;
    this.initMarquee();
    
    this.resizeTimeout = null;
    this.handleResize = () => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        const currentWidth = this.offsetWidth;
        // Only reinitialize if width actually changed (not just height from mobile scroll)
        if (Math.abs(currentWidth - this.lastWidth) > 1) {
          this.lastWidth = currentWidth;
          this.reinitMarquee();
        }
      }, 250);
    };
    
    window.addEventListener('resize', this.handleResize);

    this.addEventListener("focusin", () => {
      if (this.loop) this.loop.pause();
    });

    this.addEventListener("focusout", () => {
      if (this.loop) this.loop.play();
    });
  }
  
  initMarquee() {
    // Calculate how many times we need to duplicate items to fill the width
    const containerWidth = this.offsetWidth;
    let totalItemsWidth = 0;
    this.originalItems.forEach(item => {
      totalItemsWidth += item.offsetWidth;
    });
    if (!totalItemsWidth) return;
    
    // Duplicate items until we have slightly more than the container width for seamless loop
    const minWidth = containerWidth * 1.1;
    let duplications = Math.ceil(minWidth / totalItemsWidth);
    
    // Ensure at least 2 sets for smooth looping
    duplications = Math.max(duplications, 2);
    
    for (let i = 1; i < duplications; i++) {
      this.originalItems.forEach(item => {
        const clone = item.cloneNode(true);
        clone.classList.remove('marquee__item--original');
        clone.setAttribute("aria-hidden", "true");
        clone.setAttribute("tabindex", "-1");
        this.track.appendChild(clone);
      });
    }

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!window.animations_enabled || prefersReducedMotion || parseFloat(this.dataset.speed) === 0) return;
    const allItems = this.querySelectorAll(".marquee__item");

    this.loop = horizontalLoop(allItems, {
      repeat: -1,
      speed: parseFloat(this.dataset.speed) || 1,
      reversed: this.dataset.reversed === "true"
    });
    
    if (this.dataset.pauseOnHover === "true") {
      this.addEventListener("mouseenter", () => this.loop.pause());
      this.addEventListener("mouseleave", () => this.loop.play());
    }
  }
  
  reinitMarquee() {
    if (this.loop) {
      this.loop.kill();
    }
    
    const clonedItems = this.track.querySelectorAll('.marquee__item:not(.marquee__item--original)');
    clonedItems.forEach(item => item.remove());
    
    if (window.animations_enabled) {
      gsap.set(this.originalItems, { clearProps: "all" });
    }
    
    this.initMarquee();
  }

  disconnectedCallback() {
    if (this.loop) this.loop.kill();
    window.removeEventListener('resize', this.handleResize);
    clearTimeout(this.resizeTimeout);
  }
}


customElements.define("marquee-component", MarqueeComponent);